import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Users,
  Search,
  Shield,
  Mail,
  Phone,
  Plus,
  Trash2,
  Power,
  PowerOff,
  Eye,
  EyeOff,
  Loader2,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    nationality: '',
    role: 'CUSTOMER'
  });

  // Edit user dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Status toggle loading
  const [statusLoading, setStatusLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (roleFilter) params.role = roleFilter;
      const response = await axios.get(`${API}/users`, { params });
      setUsers(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name || '', email: u.email || '', phone: u.phone || '' });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditLoading(true);
    try {
      await axios.put(`${API}/users/${editTarget.id}`, editForm);
      toast.success('User updated successfully');
      setEditTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setStatusLoading(userId);
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`);
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setStatusLoading(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success(`User ${newUser.email} created successfully`);
      setShowCreateDialog(false);
      setNewUser({ name: '', email: '', password: '', phone: '', nationality: '', role: 'CUSTOMER' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/users/${deleteTarget.id}`);
      toast.success(`User ${deleteTarget.email} deleted`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (targetUser) => {
    setStatusLoading(targetUser.id);
    try {
      const response = await axios.patch(`${API}/users/${targetUser.id}/status`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusLoading(null);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      ADMIN: 'bg-red-100 text-red-800',
      CUSTOMER_SERVICE: 'bg-blue-100 text-blue-800',
      OWNER: 'bg-green-100 text-green-800',
      CUSTOMER: 'bg-purple-100 text-purple-800'
    };
    return <Badge className={styles[role] || 'bg-gray-100'}>{role?.replace('_', ' ')}</Badge>;
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">User Management</h1>
          <p className="text-[#194a50]/70 mt-1">{users.length} users total</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) setNewUser({ name: '', email: '', password: '', phone: '', role: 'CUSTOMER' });
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#194a50] hover:bg-[#10555e] rounded-full" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Create New User</DialogTitle>
              <DialogDescription>Add a new user to the system with any role.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Password * (min 8 characters)</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="+34 600 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label>Nationality</Label>
                <Input
                  value={newUser.nationality}
                  onChange={(e) => setNewUser({ ...newUser, nationality: e.target.value })}
                  placeholder="e.g. Spanish"
                />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="CUSTOMER_SERVICE">Customer Service</SelectItem>
                    <SelectItem value="OWNER">Owner</SelectItem>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" disabled={createLoading}>
                  {createLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="shadow-sm border-[#194a50]/5">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#194a50]/50" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#194a50]/20"
                data-testid="search-input"
              />
            </div>
            <Select value={roleFilter || "all"} onValueChange={(val) => setRoleFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-48" data-testid="role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="ADMIN">Admin</SelectItem>
                <SelectItem value="CUSTOMER_SERVICE">Customer Service</SelectItem>
                <SelectItem value="OWNER">Owner</SelectItem>
                <SelectItem value="CUSTOMER">Customer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="shadow-sm border-[#194a50]/5" data-testid="users-table">
        <CardContent className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-[#194a50]/30" />
              <h3 className="text-xl font-semibold text-[#0c2431] mb-2">No users found</h3>
              <p className="text-[#194a50]/60">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Change Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#194a50] flex items-center justify-center text-white font-medium flex-shrink-0">
                            {u.name?.charAt(0) || 'U'}
                          </div>
                          <span className="font-medium text-[#0c2431]">{u.name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-[#194a50]">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          {u.email}
                        </div>
                      </td>
                      <td>
                        {u.phone ? (
                          <div className="flex items-center gap-2 text-[#194a50]/70">
                            <Phone className="w-4 h-4" />
                            {u.phone}
                          </div>
                        ) : (
                          <span className="text-[#194a50]/40">-</span>
                        )}
                      </td>
                      <td>{getRoleBadge(u.role)}</td>
                      <td>
                        <Badge className={u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        {u.id !== user?.id && (
                          <Select
                            value={u.role}
                            onValueChange={(val) => handleRoleChange(u.id, val)}
                          >
                            <SelectTrigger className="w-40" data-testid={`role-select-${u.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ADMIN">Admin</SelectItem>
                              <SelectItem value="CUSTOMER_SERVICE">Customer Service</SelectItem>
                              <SelectItem value="OWNER">Owner</SelectItem>
                              <SelectItem value="CUSTOMER">Customer</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td>
                        {u.id !== user?.id && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit user"
                              onClick={() => openEditDialog(u)}
                              className="h-8 w-8 p-0 text-[#194a50]/60 hover:text-[#194a50]"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title={u.is_active ? 'Deactivate user' : 'Activate user'}
                              onClick={() => handleToggleStatus(u)}
                              disabled={statusLoading === u.id}
                              className="h-8 w-8 p-0 text-[#194a50]/60 hover:text-[#194a50]"
                            >
                              {statusLoading === u.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : u.is_active ? (
                                <PowerOff className="w-4 h-4" />
                              ) : (
                                <Power className="w-4 h-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Delete user"
                              onClick={() => setDeleteTarget(u)}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit User</DialogTitle>
            <DialogDescription>Update name, email, or phone for {editTarget?.name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+34 600 000 000"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" disabled={editLoading}>
                {editLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete User</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to permanently delete{' '}
              <span className="font-semibold text-[#0c2431]">{deleteTarget?.name}</span>{' '}
              ({deleteTarget?.email})?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteUser}
              disabled={deleteLoading}
            >
              {deleteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
